import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Modal, Typography, generateUtilityClasses } from '@mui/material'
import { styled } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import DropdownFieldSearch from '../../../../ui/DropdownFieldSearch'
import { CustomCircular, TextField } from '../../../../ui'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import { useEditPaymentCheckMutation, useAddPaymentCheckMutation, useLazyPaymentCheckDataQuery } from '../../../../services'
import { UspConfigurationPaymentCheckTypeViewResult } from '../../../../models/apiTypes'

const classes = generateUtilityClasses('Box', ['root', 'row', 'buttonWrap', 'saveBtn'])

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '390px',
    backgroundColor: theme.palette.background.main,
    boxShadow: 24,
    padding: theme.spacing(3),
  },
  [`& .${classes.row}`]: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  [`& .${classes.buttonWrap}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    padding: theme.spacing(3, 0, 2, 0),
    marginTop: 'auto',
  },
  [`& .${classes.saveBtn}`]: {
    marginLeft: theme.spacing(2),
  },
}))

interface Props {
  rowData: UspConfigurationPaymentCheckTypeViewResult
  open: boolean
  onClose: () => void
  type: string
  submit: (...args: any) => any
}

const AddEditPaymentCheckTypeModal = ({ open, onClose, rowData, type, submit }: Props) => {
  const [initialValues, setInitialValues] = useState({})
  const [addPaymentCheckType, { isLoading: isLoadingPaymentCheckType, isSuccess: isSuccessPaymentCheckType }] = useAddPaymentCheckMutation()
  const [editPaymentCheckType, { isLoading: isLoadingEditPaymentCheckType, isSuccess: isSuccessEditPaymentCheckType }] = useEditPaymentCheckMutation()
  const [getPaymentCheckTypeData, { isLoading: isLoadingGetPaymentCheckType, isSuccess: isSuccessGetPaymentCheckType, data }] =
    useLazyPaymentCheckDataQuery()

  const handleSubmit = (values: any) => {
    if (type === 'edit') {
      editPaymentCheckType({
        paymentCheckTypeId: rowData?.ID?.toString(),
        uspConfigurationPaymentCheckTypeAddEdit: {
          checkType: values.Description,
          isIRS8300: values.IncludeOnIRS,
          isActive: values.IsActive,
        },
      })
    } else {
      addPaymentCheckType({
        uspConfigurationPaymentCheckTypeAddEdit: {
          checkType: values.Description,
          isIRS8300: values.IncludeOnIRS,
          isActive: values.IsActive,
        },
      })
    }
  }

  useEffect(() => {
    if (open && rowData && type === 'edit')
      getPaymentCheckTypeData({
        paymentCheckTypeId: rowData?.ID?.toString() as string,
      })
  }, [open, type, rowData])

  useEffect(() => {
    if (type === 'add') {
      setInitialValues({
        Description: '',
        IsActive: true,
        IncludeOnIRS: false,
      })
    }
    if (isSuccessGetPaymentCheckType && type === 'edit') {
      setInitialValues({
        Description: data?.CheckType,
        IncludeOnIRS: data?.IsIRS8300,
        IsActive: data?.IsActive,
      })
    }
  }, [isSuccessGetPaymentCheckType, data, type])

  useEffect(() => {
    if (isSuccessPaymentCheckType || isSuccessEditPaymentCheckType) {
      onClose()
      submit()
    }
  }, [isSuccessPaymentCheckType, isSuccessEditPaymentCheckType])

  const isLoading = useMemo(
    () => isLoadingPaymentCheckType || isLoadingEditPaymentCheckType || isLoadingGetPaymentCheckType,
    [isLoadingPaymentCheckType, isLoadingEditPaymentCheckType, isLoadingGetPaymentCheckType],
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Check Type</DialogTitle>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit }: any) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} py={1}>
                <Grid item xs={12}>
                  <TextField required name='Description' label='Description' fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IncludeOnIRS'
                    label='Include on IRS Form 8300?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsActive'
                    label='Is Active?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <LoadingButton type='submit' className={classes.saveBtn} variant='contained'>
                Save
              </LoadingButton>
              <Button variant='contained' color='error' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </form>
        )}
      </Form>
      <CustomCircular open={isLoading} />
    </Dialog>
  )
}

export default AddEditPaymentCheckTypeModal
